import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { formatGBP } from "../lib/format";
import { toast } from "sonner";
import { Package, ChartLineUp, ShoppingCart, Gear, Plus, Pencil, Trash, Truck, Star, Bank } from "@phosphor-icons/react";
import VendorPayouts from "../components/vendor/VendorPayouts";

const CATEGORIES = ["Dry Foods", "Fresh Produce", "Protein", "Spices", "Oils", "Flours", "Kits", "Sauces"];

export default function VendorDashboard() {
  const [tab, setTab] = useState("overview");
  const [vendor, setVendor] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editProduct, setEditProduct] = useState(null);

  const load = async () => {
    try {
      const [v, a, p, o] = await Promise.all([
        api.get("/vendors/mine/current").catch(() => ({ data: null })),
        api.get("/vendor-dashboard/analytics").catch(() => ({ data: null })),
        api.get("/vendors/mine/current").then((r) => api.get(`/products?vendor_id=${r.data.vendor_id}`)).catch(() => ({ data: [] })),
        api.get("/orders/vendor").catch(() => ({ data: [] })),
      ]);
      setVendor(v.data);
      setAnalytics(a.data);
      setProducts(p.data);
      setOrders(o.data);
    } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[VendorDashboard] non-fatal error:", err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[#5C5C54]">Loading dashboard...</div>;

  if (!vendor) {
    return (
      <div className="pt-24 pb-24 max-w-2xl mx-auto px-4 text-center">
        <p className="text-sm text-[#5C5C54] mb-6">You don't have a vendor account yet.</p>
        <Link to="/become-vendor" className="btn-primary">Become a vendor</Link>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-24 max-w-7xl mx-auto px-4 md:px-8 lg:px-12" data-testid="vendor-dashboard">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl md:text-4xl font-black tracking-tighter">{vendor.name}</h1>
          <p className="text-xs text-[#5C5C54]">
            Status: <span className={`chip ml-1 capitalize ${vendor.status === "approved" ? "bg-[#2B5329]/10 text-[#2B5329] border-[#2B5329]/30" : "bg-[#E4A951]/15 text-[#8A6A1F] border-[#E4A951]/40"}`}>{vendor.status}</span>
          </p>
        </div>
      </div>

      {vendor.status !== "approved" && (
        <div className="mb-6 p-4 rounded-xl bg-[#E4A951]/15 border border-[#E4A951]/40 text-sm text-[#8A6A1F]">
          Your store is awaiting admin approval. Products will only appear publicly once approved.
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-[#E5E2D9] mb-8 overflow-x-auto no-scrollbar">
        {[
          { k: "overview", label: "Overview", icon: <ChartLineUp size={16} weight="duotone" /> },
          { k: "products", label: "Products", icon: <Package size={16} weight="duotone" /> },
          { k: "orders", label: "Orders", icon: <ShoppingCart size={16} weight="duotone" /> },
          { k: "payouts", label: "Payouts", icon: <Bank size={16} weight="duotone" /> },
          { k: "settings", label: "Settings", icon: <Gear size={16} weight="duotone" /> },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`vendor-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${tab === t.k ? "border-[#A94438] text-[#A94438]" : "border-transparent text-[#5C5C54] hover:text-[#1A1A18]"}`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && analytics && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="card-surface p-5">
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1">Total revenue</p>
            <p className="font-heading text-3xl font-black">{formatGBP(analytics.revenue.total)}</p>
            <p className="text-xs text-[#5C5C54] mt-1">All-time</p>
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1">Orders</p>
            <p className="font-heading text-3xl font-black">{analytics.orders.total}</p>
            <p className="text-xs text-[#5C5C54] mt-1">{analytics.orders.today} today · {analytics.orders.this_week} this week</p>
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1">Pending action</p>
            <p className="font-heading text-3xl font-black text-[#A94438]">{analytics.orders.pending_action}</p>
            <p className="text-xs text-[#5C5C54] mt-1">Orders awaiting update</p>
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1">Products</p>
            <p className="font-heading text-3xl font-black">{analytics.products.total}</p>
            <p className="text-xs text-[#5C5C54] mt-1">{analytics.products.low_stock} low stock</p>
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1">Rating</p>
            <p className="font-heading text-3xl font-black flex items-center gap-1">
              {analytics.revenue.ratings_avg ? analytics.revenue.ratings_avg.toFixed(1) : "—"}
              {analytics.revenue.ratings_avg ? <Star size={20} weight="fill" className="text-[#E4A951]" /> : null}
            </p>
            <p className="text-xs text-[#5C5C54] mt-1">{analytics.revenue.ratings_count} reviews</p>
          </div>
          <div className="card-surface p-5">
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-2">Best sellers</p>
            {analytics.best_products.slice(0, 3).map((p, i) => (
              <div key={p.product_id} className="flex justify-between items-center py-1.5 text-xs">
                <span className="truncate pr-2">{i + 1}. {p.name}</span>
                <span className="font-bold flex-shrink-0">{p.total_sold} sold</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "products" && (
        <div>
          <div className="flex justify-between items-center mb-5">
            <p className="text-sm text-[#5C5C54]">{products.length} products</p>
            <button data-testid="add-product-btn" onClick={() => { setEditProduct(null); setShowProductModal(true); }} className="btn-primary text-xs gap-1">
              <Plus size={14} weight="bold" /> Add product
            </button>
          </div>
          <div className="space-y-2">
            {products.map((p) => (
              <div key={p.product_id} className="card-surface p-4 flex items-center gap-4" data-testid="vendor-product-row">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#F2EFE9]">
                  {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-bold text-sm truncate">{p.name}</p>
                  <p className="text-xs text-[#5C5C54]">{p.category} · Stock: {p.stock_quantity}</p>
                </div>
                <p className="font-heading font-black text-base">{formatGBP(p.price)}</p>
                <button data-testid="edit-product-btn" onClick={() => { setEditProduct(p); setShowProductModal(true); }} className="p-2 rounded-full hover:bg-[#F2EFE9]" title="Edit">
                  <Pencil size={16} weight="duotone" />
                </button>
              </div>
            ))}
            {products.length === 0 && <p className="text-sm text-[#5C5C54] text-center py-8">No products yet. Add your first one.</p>}
          </div>
          {showProductModal && (
            <ProductModal
              product={editProduct}
              onClose={() => { setShowProductModal(false); load(); }}
            />
          )}
        </div>
      )}

      {tab === "orders" && (
        <VendorOrders orders={orders} onChange={load} />
      )}

      {tab === "payouts" && <VendorPayouts />}

      {tab === "settings" && (
        <VendorSettings vendor={vendor} onUpdated={load} />
      )}
    </div>
  );
}

function VendorOrders({ orders, onChange }) {
  const updateStatus = async (order_id, status) => {
    try {
      await api.post(`/orders/${order_id}/status`, { status });
      toast.success(`Order marked as ${status}`);
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  if (orders.length === 0) {
    return <p className="text-sm text-[#5C5C54] text-center py-8">No orders yet.</p>;
  }

  const nextStatus = (s) => {
    const flow = { pending: "accepted", accepted: "preparing", preparing: "dispatched", dispatched: "delivered" };
    return flow[s];
  };

  return (
    <div className="space-y-2">
      {orders.map((o) => (
        <div key={o.order_id} className="card-surface p-4 flex items-center gap-4" data-testid="vendor-order-row">
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm">Order #{o.order_id.slice(-8)}</p>
            <p className="text-xs text-[#5C5C54]">{new Date(o.created_at).toLocaleString("en-GB")} · {formatGBP(o.total)}</p>
          </div>
          <span className="chip capitalize" data-testid="order-status-chip">{o.status}</span>
          {nextStatus(o.status) && (
            <button
              data-testid={`advance-order-${o.order_id}`}
              onClick={() => updateStatus(o.order_id, nextStatus(o.status))}
              className="btn-primary text-xs"
            >
              Mark {nextStatus(o.status)}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

function ProductModal({ product, onClose }) {
  const [form, setForm] = useState({
    name: product?.name || "",
    description: product?.description || "",
    category: product?.category || "Dry Foods",
    price: product?.price || 0,
    stock_quantity: product?.stock_quantity || 0,
    images: product?.images || [""],
    cultural_tags: product?.cultural_tags?.join(", ") || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock_quantity: parseInt(form.stock_quantity),
        cultural_tags: form.cultural_tags.split(",").map((t) => t.trim()).filter(Boolean),
        images: form.images.filter(Boolean),
      };
      if (product) {
        await api.put(`/products/${product.product_id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="product-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E5E2D9] flex items-center justify-between">
          <h2 className="font-heading text-xl font-bold">{product ? "Edit product" : "Add product"}</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-[#F2EFE9]">×</button>
        </div>
        <div className="p-6 space-y-3">
          <input data-testid="product-name-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" />
          <textarea data-testid="product-desc-input" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field resize-none" />
          <select data-testid="product-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-field">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input type="number" step="0.01" data-testid="product-price-input" placeholder="Price (£)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input-field" />
            <input type="number" data-testid="product-stock-input" placeholder="Stock" value={form.stock_quantity} onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })} className="input-field" />
          </div>
          <input data-testid="product-image-input" placeholder="Image URL" value={form.images[0]} onChange={(e) => setForm({ ...form, images: [e.target.value] })} className="input-field" />
          <input data-testid="product-tags-input" placeholder="Cultural tags (comma separated)" value={form.cultural_tags} onChange={(e) => setForm({ ...form, cultural_tags: e.target.value })} className="input-field" />
        </div>
        <div className="p-6 border-t border-[#E5E2D9] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
          <button onClick={save} data-testid="save-product-btn" disabled={saving} className="btn-primary text-sm disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VendorSettings({ vendor, onUpdated }) {
  const [form, setForm] = useState({
    name: vendor.name,
    description: vendor.description,
    cover_image_url: vendor.cover_image_url,
    delivery_config: { ...vendor.delivery_config },
    contact: { ...vendor.contact },
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/vendors/${vendor.vendor_id}`, form);
      toast.success("Settings saved");
      onUpdated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  return (
    <div className="card-surface p-6 space-y-4 max-w-2xl">
      <h3 className="font-heading text-lg font-bold">Store settings</h3>
      <input data-testid="settings-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" placeholder="Name" />
      <textarea data-testid="settings-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-field resize-none" />
      <input data-testid="settings-cover" value={form.cover_image_url} onChange={(e) => setForm({ ...form, cover_image_url: e.target.value })} className="input-field" placeholder="Cover image URL" />

      <h3 className="font-heading text-lg font-bold pt-3 border-t border-[#E5E2D9]">Delivery</h3>
      <div className="grid md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[#5C5C54]">Base postcode</label>
          <input data-testid="settings-base-pc" value={form.delivery_config.base_postcode} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, base_postcode: e.target.value.toUpperCase() } })} className="input-field uppercase" />
        </div>
        <div>
          <label className="text-xs text-[#5C5C54]">Min. order (£)</label>
          <input type="number" step="0.01" data-testid="settings-min-order" value={form.delivery_config.minimum_order} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, minimum_order: parseFloat(e.target.value) || 0 } })} className="input-field" />
        </div>
        <div>
          <label className="text-xs text-[#5C5C54]">Local fee (£)</label>
          <input type="number" step="0.01" data-testid="settings-local-fee" value={form.delivery_config.local_delivery_fee} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, local_delivery_fee: parseFloat(e.target.value) || 0 } })} className="input-field" />
        </div>
        <div>
          <label className="text-xs text-[#5C5C54]">Nationwide fee (£)</label>
          <input type="number" step="0.01" data-testid="settings-nationwide-fee" value={form.delivery_config.nationwide_shipping_fee} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, nationwide_shipping_fee: parseFloat(e.target.value) || 0 } })} className="input-field" />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input data-testid="settings-nationwide-toggle" type="checkbox" checked={form.delivery_config.nationwide_shipping_enabled} onChange={(e) => setForm({ ...form, delivery_config: { ...form.delivery_config, nationwide_shipping_enabled: e.target.checked } })} />
        UK-wide nationwide shipping
      </label>

      <button data-testid="save-settings-btn" onClick={save} disabled={saving} className="btn-primary">
        {saving ? "Saving..." : "Save settings"}
      </button>
    </div>
  );
}
